/* ============================================
   ARLOURA — React Spring Animation Module
   Spring physics animations embedded in
   Shopify Liquid theme
   ============================================ */

// Minimal React + ReactDOM + React-Spring bundle
// Provides spring-based animations for homepage sections

(function() {
  'use strict';

  // ── Spring physics engine (standalone, no React dependency) ──
  class SpringAnimator {
    constructor(config = {}) {
      this.tension = config.tension || 170;
      this.friction = config.friction || 26;
      this.mass = config.mass || 1;
      this.velocity = config.velocity || 0;
      this.from = config.from || 0;
      this.to = config.to || 1;
      this.progress = this.from;
      this.currentVelocity = this.velocity;
      this.isAnimating = false;
      this.onUpdate = config.onUpdate || (() => {});
      this.onComplete = config.onComplete || (() => {});
      this._raf = null;
    }

    start() {
      this.isAnimating = true;
      this._lastTime = performance.now();
      this._animate();
      return this;
    }

    stop() {
      this.isAnimating = false;
      if (this._raf) cancelAnimationFrame(this._raf);
      return this;
    }

    setTarget(to) {
      this.to = to;
      if (!this.isAnimating) this.start();
      return this;
    }

    _animate() {
      if (!this.isAnimating) return;

      const now = performance.now();
      const dt = Math.min((now - this._lastTime) / 1000, 0.064);
      this._lastTime = now;

      const displacement = this.progress - this.to;
      const springForce = -this.tension * displacement;
      const dampingForce = -this.friction * this.currentVelocity;
      const acceleration = (springForce + dampingForce) / this.mass;

      this.currentVelocity += acceleration * dt;
      this.progress += this.currentVelocity * dt;

      this.onUpdate(this.progress, this.currentVelocity);

      const isSettled =
        Math.abs(this.currentVelocity) < 0.01 &&
        Math.abs(this.progress - this.to) < 0.001;

      if (isSettled) {
        this.progress = this.to;
        this.currentVelocity = 0;
        this.isAnimating = false;
        this.onUpdate(this.progress, 0);
        this.onComplete();
        return;
      }

      this._raf = requestAnimationFrame(() => this._animate());
    }
  }

  // ── Staggered spring animations for lists ──
  class SpringStagger {
    constructor(config = {}) {
      this.items = config.items || [];
      this.tension = config.tension || 200;
      this.friction = config.friction || 24;
      this.staggerDelay = config.staggerDelay || 60;
      this.from = config.from || { opacity: 0, y: 40, scale: 0.95 };
      this.to = config.to || { opacity: 1, y: 0, scale: 1 };
      this.animators = [];
    }

    animate() {
      this.items.forEach((item, i) => {
        setTimeout(() => {
          const anim = {
            opacity: new SpringAnimator({
              tension: this.tension,
              friction: this.friction,
              from: this.from.opacity,
              to: this.to.opacity,
              onUpdate: (v) => { anim._values.opacity = v; this._apply(item, anim._values); }
            }),
            y: new SpringAnimator({
              tension: this.tension,
              friction: this.friction,
              from: this.from.y,
              to: this.to.y,
              onUpdate: (v) => { anim._values.y = v; this._apply(item, anim._values); }
            }),
            scale: new SpringAnimator({
              tension: this.tension,
              friction: this.friction,
              from: this.from.scale,
              to: this.to.scale,
              onUpdate: (v) => { anim._values.scale = v; this._apply(item, anim._values); }
            }),
            _values: { ...this.from }
          };

          anim.opacity.start();
          anim.y.start();
          anim.scale.start();
          this.animators.push(anim);
        }, i * this.staggerDelay);
      });
    }

    _apply(el, values) {
      el.style.opacity = values.opacity;
      el.style.transform = `translateY(${values.y}px) scale(${values.scale})`;
    }

    reset() {
      this.animators.forEach(a => {
        a.opacity.stop();
        a.y.stop();
        a.scale.stop();
      });
      this.animators = [];
      this.items.forEach(el => {
        el.style.opacity = this.from.opacity;
        el.style.transform = `translateY(${this.from.y}px) scale(${this.from.scale})`;
      });
    }
  }

  // ── Spring scroll reveal ──
  class SpringScrollReveal {
    constructor(element, config = {}) {
      this.element = element;
      this.tension = config.tension || 180;
      this.friction = config.friction || 22;
      this.from = config.from || { opacity: 0, y: 50 };
      this.to = config.to || { opacity: 1, y: 0 };
      this.threshold = config.threshold || 0.15;
      this.animator = null;
      this.revealed = false;

      this._applyInitial();
      this._observe();
    }

    _applyInitial() {
      this.element.style.opacity = this.from.opacity;
      this.element.style.transform = `translateY(${this.from.y}px)`;
      this.element.style.willChange = 'opacity, transform';
    }

    _observe() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.revealed) {
            this.revealed = true;
            this._animate();
          }
        });
      }, { threshold: this.threshold, rootMargin: '0px 0px -50px 0px' });

      observer.observe(this.element);
    }

    _animate() {
      const anim = {
        opacity: new SpringAnimator({
          tension: this.tension,
          friction: this.friction,
          from: this.from.opacity,
          to: this.to.opacity,
          onUpdate: (v) => { anim._opacity = v; this._apply(anim); }
        }),
        y: new SpringAnimator({
          tension: this.tension,
          friction: this.friction,
          from: this.from.y,
          to: this.to.y,
          onUpdate: (v) => { anim._y = v; this._apply(anim); }
        }),
        _opacity: this.from.opacity,
        _y: this.from.y
      };

      anim.opacity.start();
      anim.y.start();
      this.animator = anim;
    }

    _apply(anim) {
      this.element.style.opacity = anim._opacity;
      this.element.style.transform = `translateY(${anim._y}px)`;
    }
  }

  // ── Magnetic spring button ──
  class SpringMagnetic {
    constructor(element, config = {}) {
      this.element = element;
      this.tension = config.tension || 300;
      this.friction = config.friction || 20;
      this.strength = config.strength || 0.3;
      this.animX = null;
      this.animY = null;
      this.currentX = 0;
      this.currentY = 0;

      this._onMouseMove = this._onMouseMove.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);

      this.element.addEventListener('mousemove', this._onMouseMove);
      this.element.addEventListener('mouseleave', this._onMouseLeave);
    }

    _onMouseMove(e) {
      const rect = this.element.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) * this.strength;
      const y = (e.clientY - rect.top - rect.height / 2) * this.strength;

      if (this.animX) this.animX.stop();
      if (this.animY) this.animY.stop();

      this.animX = new SpringAnimator({
        tension: this.tension,
        friction: this.friction,
        from: this.currentX,
        to: x,
        onUpdate: (v) => {
          this.currentX = v;
          this.element.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
        }
      });

      this.animY = new SpringAnimator({
        tension: this.tension,
        friction: this.friction,
        from: this.currentY,
        to: y,
        onUpdate: (v) => {
          this.currentY = v;
          this.element.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
        }
      });

      this.animX.start();
      this.animY.start();
    }

    _onMouseLeave() {
      if (this.animX) this.animX.stop();
      if (this.animY) this.animY.stop();

      this.animX = new SpringAnimator({
        tension: this.tension,
        friction: this.friction,
        from: this.currentX,
        to: 0,
        onUpdate: (v) => {
          this.currentX = v;
          this.element.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
        }
      });

      this.animY = new SpringAnimator({
        tension: this.tension,
        friction: this.friction,
        from: this.currentY,
        to: 0,
        onUpdate: (v) => {
          this.currentY = v;
          this.element.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
        }
      });

      this.animX.start();
      this.animY.start();
    }

    destroy() {
      this.element.removeEventListener('mousemove', this._onMouseMove);
      this.element.removeEventListener('mouseleave', this._onMouseLeave);
      if (this.animX) this.animX.stop();
      if (this.animY) this.animY.stop();
    }
  }

  // ── Spring parallax ──
  class SpringParallax {
    constructor(element, config = {}) {
      this.element = element;
      this.speed = config.speed || 0.1;
      this.tension = config.tension || 120;
      this.friction = config.friction || 20;
      this.currentOffset = 0;
      this.anim = null;
    }

    update(scrollY) {
      const rect = this.element.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const viewCenter = window.innerHeight / 2;
      const targetOffset = (centerY - viewCenter) * this.speed;

      if (this.anim) this.anim.stop();

      this.anim = new SpringAnimator({
        tension: this.tension,
        friction: this.friction,
        from: this.currentOffset,
        to: targetOffset,
        onUpdate: (v) => {
          this.currentOffset = v;
          this.element.style.transform = `translate3d(0, ${v}px, 0)`;
        }
      });

      this.anim.start();
    }
  }

  // ── Spring scale on hover ──
  class SpringHover {
    constructor(element, config = {}) {
      this.element = element;
      this.tension = config.tension || 250;
      this.friction = config.friction || 18;
      this.scaleFrom = config.scaleFrom || 1;
      this.scaleTo = config.scaleTo || 1.03;
      this.currentScale = this.scaleFrom;
      this.anim = null;

      this.element.addEventListener('mouseenter', () => this._animateTo(this.scaleTo));
      this.element.addEventListener('mouseleave', () => this._animateTo(this.scaleFrom));
    }

    _animateTo(target) {
      if (this.anim) this.anim.stop();

      this.anim = new SpringAnimator({
        tension: this.tension,
        friction: this.friction,
        from: this.currentScale,
        to: target,
        onUpdate: (v) => {
          this.currentScale = v;
          this.element.style.transform = `scale(${v})`;
        }
      });

      this.anim.start();
    }
  }

  // ── Initialize on DOM ready ──
  document.addEventListener('DOMContentLoaded', () => {

    // Spring scroll reveals
    document.querySelectorAll('[data-spring-reveal]').forEach(el => {
      new SpringScrollReveal(el, {
        tension: parseInt(el.dataset.springTension) || 180,
        friction: parseInt(el.dataset.springFriction) || 22,
        from: { opacity: 0, y: parseInt(el.dataset.springY) || 50 },
        to: { opacity: 1, y: 0 }
      });
    });

    // Spring magnetic buttons
    document.querySelectorAll('[data-spring-magnetic]').forEach(el => {
      new SpringMagnetic(el, {
        tension: parseInt(el.dataset.springTension) || 300,
        friction: parseInt(el.dataset.springFriction) || 20,
        strength: parseFloat(el.dataset.springStrength) || 0.3
      });
    });

    // Spring hover scale
    document.querySelectorAll('[data-spring-hover]').forEach(el => {
      new SpringHover(el, {
        tension: parseInt(el.dataset.springTension) || 250,
        friction: parseInt(el.dataset.springFriction) || 18,
        scaleTo: parseFloat(el.dataset.springScale) || 1.03
      });
    });

    // Spring parallax
    const parallaxElements = [];
    document.querySelectorAll('[data-spring-parallax]').forEach(el => {
      parallaxElements.push(new SpringParallax(el, {
        speed: parseFloat(el.dataset.springSpeed) || 0.1
      }));
    });

    if (parallaxElements.length > 0) {
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            parallaxElements.forEach(p => p.update(window.scrollY));
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    }

    // Stagger reveal for product grids
    document.querySelectorAll('[data-spring-stagger]').forEach(container => {
      const items = container.children;
      if (items.length === 0) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const stagger = new SpringStagger({
              items: Array.from(items),
              tension: 200,
              friction: 24,
              staggerDelay: 60,
              from: { opacity: 0, y: 30, scale: 0.95 },
              to: { opacity: 1, y: 0, scale: 1 }
            });
            stagger.animate();
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      observer.observe(container);
    });

    // Expose API globally
    window.ArlouraSpring = {
      SpringAnimator,
      SpringStagger,
      SpringScrollReveal,
      SpringMagnetic,
      SpringParallax,
      SpringHover
    };

  });

})();
